import type OriginTracing from '@atlassiansox/origin-tracing';
import { type IntlShape } from 'react-intl-next';

import {
	getPostExpandTemplates,
	getPreExpandTemplates,
} from '../../ui/project-pages-improvement/utils';
import { type BlueprintData } from '../../state/ui/blueprints/types';
import { type ConfluenceLicenseStatusList } from '../../state/ui/types';
import { useTemplatesPanelControlsWithLicense } from './useTemplatesControlsWithLicense';
import { useTemplatesControlsWithoutLicense } from './useTemplatesControlsWithoutLicense';
import { type AccessRequestCapabilityType } from '../../state/confluence/access-request-capabilities/types';

interface GetTemplatesParams {
	intl: IntlShape;
	isConfluenceAutoProvisioned: boolean;
	isProjectPagesSporkRebrandingEnabled: boolean;
	confluenceLicenseStatus: ConfluenceLicenseStatusList;
}

interface UseTemplatesPanelControlsParams {
	accessRequestCapability?: AccessRequestCapabilityType;
	areTemplatesEnabled: boolean;
	blueprints: BlueprintData[];
	contentId: string | null;
	hasAccessToConfluence: boolean;
	isConfluenceAutoProvisioned?: boolean;
	intl: IntlShape;
	origin: OriginTracing | null;
	spaceKey: string | null;
	showError?: () => void;
	projectId?: number;
	projectType?: string;
}

export const useTemplatesPanelControls = ({
	accessRequestCapability,
	areTemplatesEnabled,
	blueprints,
	contentId,
	hasAccessToConfluence,
	intl,
	origin,
	spaceKey,
	showError,
	projectId,
	projectType,
}: UseTemplatesPanelControlsParams) => {
	const { props: hasLicenseProps, hoverPreviewProps } = useTemplatesPanelControlsWithLicense({
		accessRequestCapability,
		areTemplatesEnabled,
		blueprints,
		contentId,
		intl,
		origin,
		spaceKey,
		showError,
		projectId,
		projectType,
	});
	const { props: hasNoLicenseProps } = useTemplatesControlsWithoutLicense({});

	const getTemplates = ({
		isConfluenceAutoProvisioned,
		isProjectPagesSporkRebrandingEnabled,
		confluenceLicenseStatus,
	}: GetTemplatesParams) =>
		hasAccessToConfluence
			? getPostExpandTemplates(
					spaceKey,
					areTemplatesEnabled,
					confluenceLicenseStatus,
					blueprints,
					intl,
					isConfluenceAutoProvisioned,
					isProjectPagesSporkRebrandingEnabled,
				)
			: getPreExpandTemplates(
					intl,
					isConfluenceAutoProvisioned,
					isProjectPagesSporkRebrandingEnabled,
				);

	return {
		getTemplates,
		props: hasAccessToConfluence ? hasLicenseProps : hasNoLicenseProps,
		hoverPreviewProps: hasAccessToConfluence ? hoverPreviewProps : undefined,
	};
};
